import { apiGet, apiRequest } from "./client.js";
import { parseOutput, parseOutputs } from "./parse.js";
import type { Output, OutputCodec, OutputVisibility } from "./types.js";

/**
 * What a feed publishes, as rows. A feed with two deliveries is two rows and no
 * special case, which is why nothing here is written in the singular.
 *
 * A create names only what differs from the baseline: everything the request leaves
 * out is the schema's own default, stated in the database and nowhere else
 * (`apps/api/src/outputs.ts:146-163`). So this app carries no default codec, no
 * default bitrate and no default address — it sends what the operator typed.
 */

export interface OutputInput {
  protocol?: string;
  codec?: OutputCodec;
  bitrate_kbps?: number;
  sample_rate_hz?: number;
  channels?: number;
  mount_path?: string;
  host?: string;
  port?: number;
  visibility?: OutputVisibility;
  published_url?: string;
  enabled?: boolean;
}

const outputsPath = (feedId: string) =>
  `/api/feeds/${encodeURIComponent(feedId)}/outputs`;

export async function fetchOutputs(
  feedId: string,
  signal?: AbortSignal,
): Promise<Output[]> {
  return parseOutputs(await apiGet<unknown>(outputsPath(feedId), { signal }));
}

export async function createOutput(
  feedId: string,
  input: OutputInput,
): Promise<Output | null> {
  return parseOutput(
    await apiRequest<unknown>(outputsPath(feedId), {
      method: "POST",
      body: input,
    }),
  );
}

export async function patchOutput(
  feedId: string,
  outputId: string,
  input: OutputInput,
): Promise<Output | null> {
  return parseOutput(
    await apiRequest<unknown>(
      `${outputsPath(feedId)}/${encodeURIComponent(outputId)}`,
      { method: "PATCH", body: input },
    ),
  );
}

export async function deleteOutput(
  feedId: string,
  outputId: string,
): Promise<void> {
  await apiRequest<void>(
    `${outputsPath(feedId)}/${encodeURIComponent(outputId)}`,
    { method: "DELETE" },
  );
}
